/**
 * NewConversationModal Component
 *
 * Modal for creating a new conversation
 */

import React, { useEffect, useMemo, useState } from "react";
import { useGetProfileQuery } from "../../../store/api/authApi";
import { CreateConversationRequest } from "../../../store/api/conversationsApi";
import { useListConversationParticipantsQuery } from "../../../store/api/usersApi";

interface NewConversationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreateConversation: (data: CreateConversationRequest) => void;
	isCreating: boolean;
	organizationId: string;
}

export const NewConversationModal: React.FC<NewConversationModalProps> = ({
	isOpen,
	onClose,
	onCreateConversation,
	isCreating,
	organizationId,
}) => {
	const [subject, setSubject] = useState("");
	const [selectedParticipants, setSelectedParticipants] = useState<string[]>(
		[],
	);
	const [searchQuery, setSearchQuery] = useState("");

	// Get current user to determine role
	const { data: currentUser } = useGetProfileQuery();

	// Fetch users for participant selection with backend search filtering
	// Backend handles search across name, email, and role
	const { data: usersResponse } = useListConversationParticipantsQuery({
		page_size: 100,
		search: searchQuery || undefined, // Pass search to backend (searches name, email, role)
	});

	const allUsers = usersResponse?.results || [];

	// Filter users based on current user's role (client-side for role filtering)
	const availableUsers = useMemo(() => {
		if (!currentUser) return allUsers;

		// If current user is a tenant, only show admins, agents, and technicians
		if (currentUser.role === "tenant") {
			return allUsers.filter(
				(user) =>
					user.role === "admin" ||
					user.role === "agent" ||
					user.role === "technician",
			);
		}

		// For admins: can message anyone (tenants, agents, technicians, other admins)
		// For agents/technicians: can message anyone (tenants, admins, other agents/technicians)
		// Exclude the current user from the list (can't message yourself)
		return allUsers.filter((user) => user.id !== currentUser.id);
	}, [allUsers, currentUser]);

	// No need for additional client-side filtering - backend handles search
	const filteredUsers = availableUsers;

	// Reset form when modal closes
	useEffect(() => {
		if (!isOpen) {
			setSubject("");
			setSelectedParticipants([]);
			setSearchQuery("");
		}
	}, [isOpen]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (subject.trim() && selectedParticipants.length > 0) {
			onCreateConversation({
				organization: organizationId,
				participants: selectedParticipants,
				subject: subject.trim(),
			});
		}
	};

	const toggleParticipant = (userId: string) => {
		setSelectedParticipants((prev) =>
			prev.includes(userId)
				? prev.filter((id) => id !== userId)
				: [...prev, userId],
		);
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
			<div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
				{/* Header */}
				<div className="flex items-center justify-between p-6 border-b border-gray-200">
					<h2 className="text-xl font-semibold text-gray-900">
						{currentUser?.role === "tenant"
							? "Message Admin, Agent, or Technician"
							: currentUser?.role === "admin"
							? "New Conversation - Message Anyone"
							: "New Conversation"}
					</h2>
					<button
						onClick={onClose}
						className="text-gray-400 hover:text-gray-600 transition-colors"
						disabled={isCreating}
					>
						<svg
							className="w-6 h-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				{/* Form */}
				<form onSubmit={handleSubmit} className="flex flex-col h-full">
					<div className="flex-1 overflow-y-auto p-6 space-y-6">
						{/* Subject */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Subject *
							</label>
							<input
								type="text"
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								placeholder="Enter conversation subject"
								required
								disabled={isCreating}
								className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
							/>
						</div>

						{/* Participants */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-2">
								{currentUser?.role === "tenant"
									? "Select Admin, Agent, or Technician *"
									: currentUser?.role === "admin"
									? "Select Participants (Tenants, Agents, Technicians, or Admins) *"
									: "Select Participants *"}{" "}
								({selectedParticipants.length} selected)
							</label>

							{/* Search */}
							<div className="relative mb-3">
								<input
									type="text"
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									placeholder="Search users..."
									disabled={isCreating}
									className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
								/>
								<svg
									className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
									/>
								</svg>
							</div>

							{/* User List */}
							<div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
								{filteredUsers.length === 0 ? (
									<div className="p-4 text-center text-gray-500">
										{searchQuery
											? "No users found matching your search"
											: currentUser?.role === "tenant"
											? "No admins, agents, or technicians available"
											: currentUser?.role === "admin"
											? "No users available to message"
											: "No users available"}
									</div>
								) : (
									filteredUsers.map((user) => (
										<label
											key={user.id}
											className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
										>
											<input
												type="checkbox"
												checked={selectedParticipants.includes(user.id)}
												onChange={() => toggleParticipant(user.id)}
												disabled={isCreating}
												className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
											/>
											<div className="ml-3 flex-1">
												<p className="text-sm font-medium text-gray-900">
													{user.first_name} {user.last_name}
												</p>
												<p className="text-xs text-gray-500">{user.email}</p>
											</div>
											<span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
												{user.role}
											</span>
										</label>
									))
								)}
							</div>

							{selectedParticipants.length === 0 && (
								<p className="mt-2 text-sm text-red-600">
									Please select at least one participant
								</p>
							)}
						</div>
					</div>

					{/* Footer */}
					<div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
						<button
							type="button"
							onClick={onClose}
							disabled={isCreating}
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={
								!subject.trim() ||
								selectedParticipants.length === 0 ||
								isCreating
							}
							className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
						>
							{isCreating ? (
								<>
									<svg
										className="animate-spin h-5 w-5 text-white"
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
									>
										<circle
											className="opacity-25"
											cx="12"
											cy="12"
											r="10"
											stroke="currentColor"
											strokeWidth="4"
										></circle>
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										></path>
									</svg>
									Creating...
								</>
							) : (
								"Create Conversation"
							)}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};
